import React, { lazy, Suspense, useEffect } from "react";
import * as Sentry from "@sentry/react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { DataVisibilityProvider } from "@/contexts/DataVisibilityContext";
import { SaleCartProvider } from "@/contexts/SaleCartContext";
import { PlatformAdminProvider } from "@/contexts/PlatformAdminContext";
import { ProtectedRoute, DriverProtectedRoute } from "@/components/auth";
import { POSProtectedRoute } from "@/components/auth/POSProtectedRoute";
import { StockCheckProtectedRoute } from "@/components/auth/StockCheckProtectedRoute";
import { AdminProtectedRoute } from "@/components/auth/AdminProtectedRoute";
import { RecoveryLockGate } from "@/components/auth/RecoveryLockGate";
import { ErrorBoundary } from "@/components/ui/error-boundary";
import { AppBadgeManager } from "@/components/AppBadgeManager";
import { UpdateNotification } from "@/components/ui/UpdateNotification";
import { useServiceWorkerUpdate } from "@/hooks/useServiceWorkerUpdate";
import { DeepLinkInitializer } from "@/components/DeepLinkInitializer";
import { preloadNotificationSounds } from "@/utils/notificationSounds";
import { PortalAuthProvider } from "./contexts/PortalAuthContext";
import { PortalProtectedRoute } from "./components/auth/PortalProtectedRoute";

// ─── Grupo 3 — Static (auth, public, shell — carregam imediatamente) ──────────
import Index from "./pages/Index";
import Landing from "./pages/Landing";
import Auth from "./pages/Auth";
import AdminLogin from "./pages/Auth/AdminLogin";
import ResetPassword from "./pages/ResetPassword";
import NotFound from "./pages/NotFound";
import { SignContract } from "./pages/SignContract";
import ConfirmLoanRead from "./pages/ConfirmLoanRead";
import Thanks from "./pages/Thanks";
import ThankYou from "./pages/ThankYou";
import AcceptInvitation from "./pages/AcceptInvitation";
import ModulesPresentation from "@/pages/ModulesPresentation";
import DriverAuth from "./pages/DriverAuth";
import DriverEntry from "./pages/DriverEntry";
import StockCheckAuth from "./pages/StockCheckAuth";
import POSAuth from "./pages/POS/POSAuth";
import FinancialAccountDetails from "./pages/FinancialAccountDetails";
import PortalAuth from "./pages/Portal/PortalAuth";
import { StockChecklistManager } from "./components/Stock/StockChecklistManager";
import TrackItemPage from "./pages/TrackItemPage";
import PaymentCallback from "./pages/PaymentCallback";
import SupplierQuotationResponse from "./pages/Public/SupplierQuotationResponse";
import StockCheckPage from "./pages/Public/StockCheckPage";
import PurchaseInvoicesPage from "./pages/dashboard/PurchaseInvoicesPage";
import { ServicesMainView } from "./components/Services/ServicesMainView";
import ServiceOrderNewPage    from "@/pages/services/ServiceOrderNewPage";
import ServiceOrderDetailPage     from "@/pages/services/ServiceOrderDetailPage";
import ServiceOrderQuoteEditorPage from "@/pages/services/ServiceOrderQuoteEditorPage";
import ProjectDetailPage from "@/pages/projects/ProjectDetailPage";

// ─── Grupo 1 — Lazy (Driver app, POS, Admin panel, Portal) ───────────────────
const DriverApp = lazy(() => import("./pages/DriverApp"));
const POSApp = lazy(() => import("./pages/POS/POSApp"));
const PortalApp = lazy(() => import("./pages/Portal/PortalApp"));
const AdminLayout = lazy(() =>
  import("./layouts/AdminLayout").then((m) => ({ default: m.AdminLayout }))
);
const AdminDashboard = lazy(() =>
  import("./pages/Admin/AdminDashboard").then((m) => ({ default: m.AdminDashboard }))
);
const AdminCompaniesView = lazy(() =>
  import("./pages/Admin/AdminCompaniesView").then((m) => ({ default: m.AdminCompaniesView }))
);
const AdminUsersView = lazy(() =>
  import("./pages/Admin/AdminUsersView").then((m) => ({ default: m.AdminUsersView }))
);
const AdminSyncMonitor = lazy(() =>
  import("./pages/Admin/AdminSyncMonitor").then((m) => ({ default: m.AdminSyncMonitor }))
);
const AdminAuditView = lazy(() =>
  import("./pages/Admin/AdminAuditView").then((m) => ({ default: m.AdminAuditView }))
);
const AdminSettings = lazy(() =>
  import("./pages/Admin/AdminSettings").then((m) => ({ default: m.AdminSettings }))
);
const AdminNotFound = lazy(() =>
  import("./pages/Admin/AdminNotFound").then((m) => ({ default: m.AdminNotFound }))
);

// ─── Grupo 2 — Lazy (Sales, Invoices, Purchases, Financial, Driver catalog) ───
const UnifiedOrderForm = lazy(() => import("./pages/Sales/UnifiedOrderForm"));
const EditOrder = lazy(() => import("./pages/Sales/EditOrder"));
const EmitNFe = lazy(() => import("./pages/Sales/EmitNFe"));
const ProductCatalogPage = lazy(() => import("./pages/Sales/ProductCatalogPage"));
const ProductDetailPage = lazy(() => import("./pages/Sales/ProductDetailPage"));
const CreateManualInvoice = lazy(() => import("./pages/Invoices/CreateManualInvoice"));
const PurchaseFormPage = lazy(() => import("./pages/Purchases/PurchaseFormPage"));
const PurchaseImportPage = lazy(() => import("./pages/Purchases/PurchaseImportPage"));
const PurchaseQuotations = lazy(() => import("./pages/Purchases/PurchaseQuotations"));
const NewPurchaseQuotation = lazy(() => import("./pages/Purchases/NewPurchaseQuotation"));
const QuotationComparison = lazy(() => import("./pages/Purchases/QuotationComparison"));
const InformReceivablePage = lazy(() => import("./pages/Financial/InformReceivablePage"));
const ReceivableFormPage = lazy(() => import("./pages/Financial/ReceivableFormPage"));
const PayableFormPage = lazy(() => import("./pages/Financial/PayableFormPage"));
const InformPayablePage = lazy(() => import("./pages/Financial/InformPayablePage"));
const DriverProductCatalogPage = lazy(() => import("./pages/DriverApp/DriverProductCatalogPage"));
const DriverProductDetailPage = lazy(() => import("./pages/DriverApp/DriverProductDetailPage"));

// ─── PageLoader — fallback enquanto chunks lazy carregam ─────────────────────
const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center bg-background">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      staleTime: 1000 * 60 * 5,    // 5 minutes
      gcTime: 1000 * 60 * 15,      // 15 minutes
      refetchOnMount: 'always',     // Refetch in background, but show cache
      retry: 1,                     // Reduz loading prolongado em falhas
    },
  },
});

// Layout wrapper para todas as páginas do dashboard
const DashboardLayout = () => {
  return (
    <ProtectedRoute>
      <Index />
    </ProtectedRoute>
  );
};

function AppWithErrorBoundary({ children }: { children: React.ReactNode }) {
  const location = useLocation();
  return (
    <ErrorBoundary resetKeys={[location.pathname]}>
      {children}
    </ErrorBoundary>
  );
}

// Fallback global — renderizado pelo Sentry.ErrorBoundary do topo, captura
// erros que escapam do ErrorBoundary por rota (ex: provider crash, router crash).
// Estilizado de forma neutra para funcionar mesmo se o Tailwind/CSS não carregar.
const GlobalErrorFallback = () => (
  <div className="min-h-screen flex items-center justify-center p-8">
    <div className="max-w-md text-center space-y-4">
      <h2 className="text-xl font-semibold text-destructive">
        Algo deu errado
      </h2>
      <p className="text-muted-foreground text-sm">
        Um erro inesperado ocorreu. Nossa equipe foi notificada automaticamente.
      </p>
      <Button onClick={() => (window.location.href = "/")}>
        Voltar ao início
      </Button>
    </div>
  </div>
);

const App = () => {
  const { needsRefresh, updateServiceWorker } = useServiceWorkerUpdate();

  // Pré-carregar sons de notificação na inicialização
  useEffect(() => {
    preloadNotificationSounds();
  }, []);

  return (
    <Sentry.ErrorBoundary fallback={<GlobalErrorFallback />} showDialog={false}>
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <AppWithErrorBoundary>
            <PlatformAdminProvider>
            <SaleCartProvider>
            <DataVisibilityProvider>
              <TooltipProvider>
              <Toaster />
              <Sonner />
              <AppBadgeManager />
              <DeepLinkInitializer />
              <UpdateNotification 
                show={needsRefresh} 
                onUpdate={updateServiceWorker}
              />
              <RecoveryLockGate />
              <Suspense fallback={<PageLoader />}>
              <Routes>
                <Route path="/" element={<Landing />} />
                <Route path="/apresentacao" element={<ModulesPresentation />} />
                <Route path="/auth" element={<Auth />} />
                <Route path="/master" element={<AdminLogin />} />
                <Route path="/reset-password" element={<ResetPassword />} />
                <Route path="/dashboard" element={<DashboardLayout />}>
                  <Route index element={<Navigate to="/dashboard/home" replace />} />
                <Route path="home" element={<></>} />
                <Route path="customers" element={<></>} />
                <Route path="suppliers" element={<></>} />
                <Route path="employees" element={<></>} />
                  <Route path="carriers" element={<></>} />
                  <Route path="deposits" element={<></>} />
                  <Route path="products" element={<></>} />
                  <Route path="products/types" element={<></>} />
                  <Route path="stock-management" element={<></>} />
                  <Route path="stock-entry" element={<></>} />
                  <Route path="stock-history" element={<></>} />
                  <Route path="stock-checklist" element={<StockChecklistManager />} />
                  <Route path="stock-balance" element={<></>} />
                  <Route path="stock-locations" element={<></>} />
                  <Route path="stock-transfers" element={<></>} />
                  <Route path="stock-transfers/:id/receive" element={<></>} />
                  <Route path="production" element={<></>} />
                  <Route path="production/new" element={<></>} />
                  <Route path="production/:projectId" element={<></>} />
                  <Route path="production-projects" element={<></>} />
                  <Route path="production-orders" element={<></>} />
                  <Route path="production-items" element={<></>} />
                  <Route path="production-qc" element={<></>} />
                  <Route path="production-operators" element={<></>} />
                  <Route path="production-work-logs" element={<></>} />
                  <Route path="production-reports" element={<></>} />
                  <Route path="production-costs" element={<></>} />
                  <Route path="production-engineering" element={<></>} />
                  <Route path="production-kanban" element={<></>} />
                  <Route path="rentals" element={<></>} />
                  <Route path="rental-assets" element={<></>} />
                  <Route path="rental-requests" element={<></>} />
                <Route path="quotations" element={<></>} />
                <Route path="purchases" element={<></>} />
                <Route path="purchases/invoices" element={<PurchaseInvoicesPage />} />
                <Route path="purchases/novo" element={<PurchaseFormPage />} />
                <Route path="purchases/importar" element={<PurchaseImportPage />} />
                <Route path="purchases/:id/editar" element={<PurchaseFormPage />} />
                <Route path="purchases/cotacoes" element={<PurchaseQuotations />} />
                <Route path="purchases/cotacoes/nova" element={<NewPurchaseQuotation />} />
                <Route path="purchases/cotacoes/:id" element={<QuotationComparison />} />
                <Route path="purchases/cotacoes/:id/comparativo" element={<QuotationComparison />} />
                <Route path="orders" element={<></>} />
                <Route path="sales-dashboard" element={<></>} />
                <Route path="sales" element={<></>} />
                <Route path="sales/novo" element={<UnifiedOrderForm />} />
                <Route path="sales/editar/:id" element={<EditOrder />} />
                <Route path="sales/editar/:id/produtos" element={<ProductCatalogPage />} />
                <Route path="sales/editar/:id/produtos/:productId" element={<ProductDetailPage />} />
                <Route path="sales/novo/produtos" element={<ProductCatalogPage />} />
                <Route path="sales/novo/produtos/:productId" element={<ProductDetailPage />} />
                <Route path="sales/nfe/:saleId" element={<EmitNFe />} />
                {/* Redirects for backward compatibility */}
                <Route path="sales/nova" element={<Navigate to="/dashboard/sales/novo" replace />} />
                <Route path="sales/orcamento" element={<Navigate to="/dashboard/sales/novo" replace />} />
                <Route path="invoices" element={<></>} />
                <Route path="invoices/nova/:invoiceType" element={<CreateManualInvoice />} />
                <Route path="logistics" element={<></>} />
                <Route path="picking" element={<></>} />
                <Route path="logistics-deliveries" element={<></>} />
                <Route path="logistics-tasks" element={<></>} />
                <Route path="logistics-fuel" element={<></>} />
                <Route path="logistics-technicians" element={<></>} />
                <Route path="logistics-cylinder-analysis" element={<></>} />
                <Route path="loans" element={<></>} />
                <Route path="financial" element={<></>} />
                <Route path="dre-management" element={<></>} />
                <Route path="dre-configuration" element={<></>} />
                <Route path="financial-cashflow" element={<></>} />
                <Route path="financial-accounts" element={<></>} />
                <Route path="financial-accounts/:accountId" element={<FinancialAccountDetails />} />
                <Route path="financial-receivables" element={<></>} />
                <Route path="financial-receivables/novo" element={<ReceivableFormPage />} />
                <Route path="financial-receivables/:id/editar" element={<ReceivableFormPage />} />
                <Route path="financial-receivables/:id/receber" element={<InformReceivablePage />} />
                <Route path="financial-payables" element={<></>} />
                <Route path="financial-payables/novo" element={<PayableFormPage />} />
                <Route path="financial-payables/:id/editar" element={<PayableFormPage />} />
                <Route path="financial-payables/:id/pagar" element={<InformPayablePage />} />
                <Route path="financial-cnab" element={<></>} />
                <Route path="financial-cost-centers" element={<></>} />
                <Route path="ecommerce" element={<></>} />
                <Route path="crm" element={<></>} />
                <Route path="crm-portfolio" element={<></>} />
                <Route path="crm-routine" element={<></>} />
                <Route path="crm-sales-assistant" element={<></>} />
                <Route path="crm-add-customers" element={<></>} />
                <Route path="financial-collections" element={<></>} />
                <Route path="financial-reconciliation" element={<></>} />
                <Route path="crm-interaction-report" element={<></>} />
                <Route path="crm-customer-map" element={<></>} />
                <Route path="crm-create-interaction" element={<></>} />
                <Route path="crm-interactions" element={<></>} />
                <Route path="crm-my-route" element={<></>} />
                <Route path="services" element={<ServicesMainView />} />
                <Route path="services/new" element={<ServiceOrderNewPage />} />
                <Route path="services/:id" element={<ServiceOrderDetailPage />} />
                <Route path="services/:id/orcamento" element={<ServiceOrderQuoteEditorPage />} />
                <Route path="tasks" element={<></>} />
                <Route path="tasks-my-tasks" element={<></>} />
                <Route path="tasks-notes" element={<></>} />
                <Route path="tasks-inbox" element={<></>} />
                <Route path="projects" element={<></>} />
                <Route path="projects/:id" element={<ProjectDetailPage />} />
                <Route path="reports" element={<></>} />
                <Route path="reports/:reportId" element={<></>} />
                <Route path="reports/center" element={<></>} />
                <Route path="reports/view/:reportId" element={<></>} />
                <Route path="reports/sale-changes" element={<></>} />
                <Route path="reports/product-analysis" element={<></>} />
                <Route path="reports/product-trend" element={<></>} />
                <Route path="reports/fiscal-products" element={<></>} />
                <Route path="reports/sales-without-items" element={<></>} />
                <Route path="reports/card-reconciliation" element={<></>} />
                <Route path="reports/scheduled" element={<></>} />
                <Route path="settings" element={<></>} />
                <Route path="fiscal/sefaz-pr" element={<></>} />
                </Route>
                <Route path="/signature/:token" element={<SignContract key={window.location.pathname} />} />
                <Route path="/cotacao/responder/:token" element={<SupplierQuotationResponse />} />
                <Route path="/track/:identifier" element={<TrackItemPage />} />
                {/* Stock Check - Authenticated stock inventory check */}
                <Route path="/conferencia-auth" element={<StockCheckAuth />} />
                <Route path="/conferencia/:token" element={
                  <StockCheckProtectedRoute>
                    <StockCheckPage />
                  </StockCheckProtectedRoute>
                } />
                <Route path="/confirm-loan-read" element={<ConfirmLoanRead />} />
                <Route path="/accept-invitation/:token" element={<AcceptInvitation />} />
                <Route path="/obrigado" element={<Thanks />} />
                <Route path="/thank-you" element={<ThankYou />} />
                {/* Driver App - Dedicated mobile-first interface for delivery drivers */}
                <Route path="/driver" element={<DriverEntry />} />
                <Route path="/driver-auth" element={<DriverEntry />} />
                <Route path="/motorista" element={
                  <DriverProtectedRoute>
                    <DriverApp />
                  </DriverProtectedRoute>
                } />
                {/* Driver Sales Product Selection */}
                <Route path="/driver/vendas/produtos" element={
                  <DriverProtectedRoute>
                    <DriverProductCatalogPage />
                  </DriverProtectedRoute>
                } />
                <Route path="/driver/vendas/produtos/:productId" element={
                  <DriverProtectedRoute>
                    <DriverProductDetailPage />
                  </DriverProtectedRoute>
                } />
                {/* POS App - Standalone point of sale interface for TouchScreen */}
                <Route path="/pdv-auth" element={<POSAuth />} />
                <Route path="/pdv" element={
                  <POSProtectedRoute>
                    <POSApp />
                  </POSProtectedRoute>
                } />
                {/* Payment Callback - Deep Link return from payment apps */}
                <Route path="/payment-callback" element={<PaymentCallback />} />
                {/* Portal do Cliente - Customer self-service portal */}
                <Route path="/portal" element={
                  <PortalAuthProvider>
                    <PortalAuth />
                  </PortalAuthProvider>
                } />
                <Route path="/portal/*" element={
                  <PortalAuthProvider>
                    <PortalProtectedRoute>
                    <PortalApp />
                    </PortalProtectedRoute>
                  </PortalAuthProvider>
                } />
                {/* Admin Routes */}
                <Route path="/admin" element={
                  <AdminProtectedRoute>
                    <AdminLayout />
                  </AdminProtectedRoute>
                }>
                  <Route index element={<AdminDashboard />} />
                  <Route path="dashboard" element={<AdminDashboard />} />
                  <Route path="companies" element={<AdminCompaniesView />} />
                  <Route path="users" element={<AdminUsersView />} />
                  <Route path="sync-monitor" element={<AdminSyncMonitor />} />
                  <Route path="audit" element={<AdminAuditView />} />
                  <Route path="settings" element={<AdminSettings />} />
                  <Route path="*" element={<AdminNotFound />} />
                </Route>
                {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
                <Route path="*" element={<NotFound />} />
              </Routes>
              </Suspense>
              </TooltipProvider>
            </DataVisibilityProvider>
            </SaleCartProvider>
            </PlatformAdminProvider>
          </AppWithErrorBoundary>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
    </Sentry.ErrorBoundary>
  );
};

export default App;
